import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { formatLaundryAddress } from '@/lib/laundryProfile';
import type { LaundryAddress } from '@/types';

export interface MapPoint {
  /** Horizontal position as a percentage (0–100). */
  x: number;
  /** Vertical position as a percentage (0–100). */
  y: number;
}

interface LocationPickerProps {
  value: MapPoint | null;
  onChange: (point: MapPoint | null) => void;
  address: LaundryAddress;
  className?: string;
}

const clamp = (value: number) => Math.min(100, Math.max(0, value));

/**
 * Clearly-labelled interactive demo map picker. No map provider is connected in
 * this preview, so instead of a fake map presented as a finished selection we
 * offer an explicit, clickable surface where the laundry drops a pin. The chosen
 * point is shown with its coordinates and alongside the typed address.
 */
export default function LocationPicker({ value, onChange, address, className }: LocationPickerProps) {
  const { t } = useTranslation();
  const addressText = formatLaundryAddress(address);

  const placeAt = (x: number, y: number) => onChange({ x: clamp(x), y: clamp(y) });

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    placeAt(((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100);
  };

  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      placeAt(50, 50);
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div
        role="button"
        tabIndex={0}
        aria-label={t('location.pick')}
        onClick={handleClick}
        onKeyDown={handleKey}
        className="relative h-56 w-full cursor-crosshair overflow-hidden rounded-lg border border-background-300 bg-background-100 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-400"
      >
        {/* stylised street grid */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(oklch(var(--background-300) / 0.65) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--background-300) / 0.65) 1px, transparent 1px)',
            backgroundSize: '38px 38px',
          }}
          aria-hidden="true"
        />
        {/* main avenues + a soft park / water hint, all token based */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(115deg, transparent 46%, oklch(var(--background-400) / 0.55) 46%, oklch(var(--background-400) / 0.55) 49%, transparent 49%), linear-gradient(20deg, transparent 60%, oklch(var(--accent-200) / 0.45) 60%, oklch(var(--accent-200) / 0.45) 62%, transparent 62%), radial-gradient(circle at 78% 24%, oklch(var(--accent-100) / 0.7), transparent 22%)',
          }}
          aria-hidden="true"
        />

        <span className="pointer-events-none absolute bottom-3 start-3 inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
          <i className="ri-flag-2-line text-sm leading-none" aria-hidden="true" />
          {t('location.demoBadge')}
        </span>

        {value ? (
          <span
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-full text-primary-600 drop-shadow"
            style={{ left: `${value.x}%`, top: `${value.y}%` }}
            aria-hidden="true"
          >
            <i className="ri-map-pin-2-fill text-3xl leading-none" />
          </span>
        ) : (
          <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <i className="ri-map-pin-add-line text-3xl leading-none text-foreground-400" aria-hidden="true" />
            <span className="text-sm font-medium text-foreground-600">{t('location.empty')}</span>
            <span className="text-xs text-foreground-400">{t('location.hint')}</span>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3.5 py-3">
        <div className="min-w-0">
          <p className="text-xs text-foreground-500">{t('location.addressLabel')}</p>
          <p className="mt-0.5 truncate text-sm font-medium text-foreground-800">
            {addressText || t('settings.address.notSet')}
          </p>
          <p className="mt-1 text-xs text-foreground-500">
            {t('location.selectedAt')}:{' '}
            {value ? (
              <span className="lb-ltr-field font-semibold text-foreground-700">
                {value.x.toFixed(1)}, {value.y.toFixed(1)}
              </span>
            ) : (
              t('location.notSelected')
            )}
          </p>
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            {t('location.clear')}
          </button>
        ) : null}
      </div>
    </div>
  );
}